import React from 'react';
import { SettingsHeaderActions } from './SettingsHeaderSlot';

// Building blocks for the settings tabs (also borrowed by Online store and a
// few other screens). Forest handoff: hairline cards with a tinted header
// band, the shared .btn-* / .input-field primitives from index.css, and the
// semantic status tones. Everything reads themed tokens, so Fable follows.

// Settings content is one column of stacked cards.
export const SettingsGrid = ({ children }) => (
  <div className="flex w-full max-w-[960px] flex-col gap-4">
    {children}
  </div>
);

// A stack of cards that shares one grid cell.
export const SettingsColumn = ({ children }) => <div className="flex flex-col gap-4">{children}</div>;

// Card with a header band (title, description, actions) when it has a title
// or actions; the body below. `planned` is a dashed placeholder card.
export const SettingsCard = ({ id, title, description, actions, wide = false, planned = false, children, className = '' }) => {
  const hasBody = React.Children.toArray(children).some((child) => child !== null && child !== false && child !== '');
  return (
    <section
      id={id}
      className={`${
        planned ? 'overflow-hidden rounded-lg border border-dashed border-gray-300' : 'card'
      } flex flex-col ${wide ? 'col-span-full' : ''} ${className}`}
    >
      {(title || actions) && (
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3 ${planned ? '' : 'bg-floral-50'} ${hasBody ? 'border-b border-gray-200' : ''}`}>
          <div className="min-w-0 flex-1">
            {title && <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>}
            {description && <p className="mt-0.5 text-[13px] text-gray-600">{description}</p>}
          </div>
          {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {hasBody && <div className="flex flex-col gap-4 px-5 py-4">{children}</div>}
    </section>
  );
};

// Label + description on the left, a control on the right. `divider` draws
// the hairline between consecutive rows.
export const SettingRow = ({ title, description, children, divider = false }) => (
  <div className={`flex items-center justify-between gap-4 ${divider ? 'border-t border-gray-200 pt-3.5' : ''}`}>
    <div className="min-w-0">
      <strong className="text-sm font-semibold text-gray-900">{title}</strong>
      {description && <p className="mt-0.5 text-[13px] text-gray-600">{description}</p>}
    </div>
    <div className="flex flex-shrink-0 items-center gap-2">{children}</div>
  </div>
);

export const PreviewPanel = ({ label = 'Preview', children }) => (
  <div className="flex flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-800">
    {label && (
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-600">{label}</span>
    )}
    {children}
  </div>
);

export const Field = ({ label, hint, htmlFor, children, className = '' }) => (
  <div className={`flex flex-col ${className}`}>
    <label htmlFor={htmlFor} className="input-label">
      {label}
      {hint && <span className="font-normal text-gray-500"> · {hint}</span>}
    </label>
    {children}
  </div>
);

export const inputClass = 'input-field';

export const textareaClass = 'input-field min-h-[96px]';

export const buttonClass = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
};

// Switch from the style guide: 34x20 track, primary fill when on. `label` is
// the visible On/Off text beside it.
export const Toggle = ({ checked, onChange, disabled = false, label, ariaLabel }) => (
  <label className={`inline-flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-[34px] flex-shrink-0 items-center rounded-full transition-colors duration-[120ms] ${
        checked ? 'bg-floral-700' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-[rgb(var(--c-on-solid))] transition-transform duration-[120ms] ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
    {label && <span className="text-[13px] font-medium text-gray-900">{label}</span>}
  </label>
);

// Status chip in the semantic tones: 'ok', 'warn', 'neutral' (also 'info',
// 'danger'). Unknown tones fall back to neutral.
const TAG_TONES = {
  ok: 'bg-ok-bg text-ok-fg',
  warn: 'bg-warn-bg text-warn-fg',
  info: 'bg-info-bg text-info-fg',
  danger: 'bg-danger-bg text-danger-fg',
  neutral: 'bg-neutral-bg text-neutral-fg',
};

export const Tag = ({ tone = 'neutral', dot = false, children, className = '' }) => (
  <span className={`inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold ${TAG_TONES[tone] || TAG_TONES.neutral} ${className}`}>
    {dot && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current" aria-hidden="true" />}
    {children}
  </span>
);

// Unsaved / Reset / Save, rendered into the page header (design: one save bar
// per batch-saving tab). Render it unconditionally; it only shows while dirty.
export const SaveBar = ({ dirty, saving = false, onReset, onSave, saveLabel = 'Save changes' }) => {
  if (!dirty && !saving) return null;
  return (
    <SettingsHeaderActions>
      <Tag tone="warn">Unsaved changes</Tag>
      <button type="button" onClick={onReset} disabled={saving} className={buttonClass.ghost}>
        Reset
      </button>
      <button type="button" onClick={onSave} disabled={saving} className={buttonClass.primary}>
        {saving ? 'Saving…' : saveLabel}
      </button>
    </SettingsHeaderActions>
  );
};
